import type { HeldSubscriptionStatus } from "./constants"

/**
 * Subscription tier matching backend configuration (Lite, Basic, Plus, Pro, Premium).
 * See: apps/backend/domain/entities/subscription.py
 */
export const SubscriptionTier = {
  TIER_1: 1,
  TIER_2: 2,
  TIER_3: 3,
  TIER_4: 4,
  TIER_5: 5
} as const

export type SubscriptionTarget = "group" | "personal"

/**
 * Status transitions:
 * - pending_payment → active (first payment succeeds)
 * - active → payment_failed (renewal fails)
 * - payment_failed → active (retry succeeds)
 * - payment_failed → expired (retries exhausted after 3 days)
 * - active|payment_failed → cancelled (user cancels)
 */
export type SubscriptionStatus = "pending_payment" | "active" | "payment_failed" | "expired" | "cancelled"

/**
 * Full subscription entity matching backend schema.
 * See: apps/backend/api/schemas/subscription.py:48-89
 * See: apps/backend/domain/entities/subscription.py
 */
export interface Subscription {
  id: number
  user_id: number
  chat_id: number
  tier: number
  stars_amount: number
  group_rating_amount: number
  rank_level: number
  status: SubscriptionStatus
  target_type: SubscriptionTarget
  telegram_subscription_id: string | null
  /** ISO datetime */
  created_at: string
  /** ISO datetime */
  first_payment_date: string | null
  /** ISO datetime */
  last_payment_date: string | null
  /** ISO datetime */
  next_billing_date: string | null
  failed_payment_attempts: number
  /** ISO datetime */
  last_failed_payment_date: string | null
  /** ISO datetime */
  retry_until_date: string | null
  /** ISO datetime */
  cancelled_at: string | null
  /** ISO datetime */
  expired_at: string | null
}

/** A subscription in one of the held states, so the cancel action and the status chip always apply. */
export interface HeldSubscription extends Subscription {
  status: HeldSubscriptionStatus
}

export interface ListSubscriptionsRequest {
  chatId?: number
}

export interface ListSubscriptionsResponse {
  subscriptions: Subscription[]
}

export type GetPersonalSubscriptionResponse = Subscription | null

export type CancelSubscriptionRequest = number
